import { useState, type FormEvent } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FileDown } from 'lucide-react';
import { Badge, type BadgeTone } from '../../components/Badge.tsx';
import { Button } from '../../components/Button.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { CopyButton } from '../../components/CopyButton.tsx';
import { Disclosure } from '../../components/Disclosure.tsx';
import { TextField } from '../../components/TextField.tsx';
import { api } from '../onboarding/api.ts';
import { useVerification } from './controller.ts';
import { DomainScopeCards } from './DomainScopeCards.tsx';
import {
  date,
  download,
  letterStatus,
  TESTING_LEVEL_NAMES,
  type DomainLetterResult,
  type TestingLevel,
} from './model.ts';
import { PublicLayout } from './PublicLayout.tsx';
import styles from './authorization.module.css';

const PAGE_LABEL = 'traceira-authz.';

// On a customer's own traceira-authz.<domain> hostname the page always shows that domain.
function pageDomain(search: { domain?: string }) {
  const host = window.location.hostname;
  return host.startsWith(PAGE_LABEL) ? host.slice(PAGE_LABEL.length) : (search.domain ?? '');
}

export function VerificationPage() {
  const search = useSearch({ strict: false }) as { domain?: string };
  const domain = pageDomain(search);
  return domain ? <DomainVerification domain={domain} /> : <DomainPrompt />;
}

function DomainPrompt() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const domain = value
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/[/:].*$/, '');
    if (domain) void navigate({ to: '/verify', search: { domain } });
  };
  return (
    <PublicLayout title="Verify a Traceira authorization">
      <section className={styles.panel}>
        <p className={styles.publicationLead}>
          Domain owners publish their Traceira authorization in their own DNS. Enter a domain to
          read that record and check Traceira’s signature.
        </p>
        <form className={styles.domainForm} onSubmit={submit}>
          <TextField label="Domain" value={value} onChange={setValue} name="domain" />
          <Button type="submit" variant="primary">
            Verify
          </Button>
        </form>
      </section>
    </PublicLayout>
  );
}

interface Verdict {
  label: string;
  tone: BadgeTone;
  shape: string;
  detail: string;
}

// One plain verdict from the signed letter itself; dates render in the reader's local time.
function verdict(domain: string, result: DomainLetterResult, verified: boolean): Verdict {
  const letter = result.letter;
  if (!letter)
    return {
      label: 'Not published',
      tone: 'attention',
      shape: '△',
      detail: `${domain} publishes no Traceira authorization in its DNS, so Traceira is not authorized to test it.`,
    };
  if (!verified)
    return {
      label: 'Signature not verified',
      tone: 'negative',
      shape: '×',
      detail: 'This record does not carry a valid Traceira signature. Do not rely on it.',
    };
  const status = letterStatus(letter);
  if (status === 'scheduled')
    return {
      label: 'Scheduled',
      tone: 'neutral',
      shape: '○',
      detail: `The owner authorized Traceira from ${date(letter.startsAt)} until ${date(letter.endsAt)}.`,
    };
  if (status === 'expired')
    return {
      label: 'Ended',
      tone: 'neutral',
      shape: '×',
      detail: `This authorization ended ${date(letter.endsAt)}.`,
    };
  if (!result.authorized)
    return {
      label: 'Outside scope',
      tone: 'attention',
      shape: '△',
      detail: `${domain} is not among the hosts this authorization covers.`,
    };
  return {
    label: 'Authorized',
    tone: 'positive',
    shape: '✓',
    detail: `The owner authorized ${letter.authorizedOperator} until ${date(letter.endsAt)}.`,
  };
}

function DomainVerification({ domain }: { domain: string }) {
  const state = useVerification(domain);
  const result = state.result;
  const letter = result?.letter ?? null;
  const verified = state.signature === 'verified';
  const record = `_traceira-authz.${letter?.domain ?? domain}`;
  const dig = `dig +short TXT ${record}`;
  const v = result ? verdict(domain, result, verified) : null;
  return (
    <PublicLayout title={`Traceira authorization · ${domain}`}>
      {state.error && (
        <p role="alert" className={styles.error}>
          {state.error}
        </p>
      )}
      <section className={styles.certificate} aria-labelledby="cert-title" aria-busy={!result}>
        <div className={styles.certTop}>
          <div className={styles.certMasthead}>
            <span className={styles.certIssuer}>Traceira</span>
            <span className={styles.certKind}>Authorization certificate</span>
          </div>
        </div>
        <h2 id="cert-title" className={styles.certDomain}>
          {letter?.domain ?? domain}
        </h2>
        {v ? (
          <>
            <Badge tone={v.tone} shape={v.shape} data-badge={v.label}>
              {v.label}
            </Badge>
            <p className={styles.lead}>{v.detail}</p>
            {letter ? (
              <p className={styles.note}>
                Read from <code>{record}</code> in the owner’s DNS · signature {state.signature}.
                Removing the record withdraws the authorization.
              </p>
            ) : (
              <p className={styles.note}>
                Looked for <code>{record}</code>. Owners publish it after approving a letter in
                Traceira.
              </p>
            )}
          </>
        ) : (
          !state.error && <p role="status">Reading the authorization from {domain}’s DNS…</p>
        )}
        <div className={styles.actions}>
          <Button onPress={() => void state.refresh()} isDisabled={state.busy}>
            {state.busy ? 'Reading…' : 'Read the record again'}
          </Button>
        </div>
      </section>
      {letter && (
        <>
          <section className={styles.panel} aria-labelledby="terms-title">
            <h2 id="terms-title">Signed terms</h2>
            <dl className={styles.facts}>
              <div>
                <dt>Testing window · your local time</dt>
                <dd>
                  <strong>
                    {date(letter.startsAt)} → {date(letter.endsAt)}
                  </strong>
                </dd>
              </div>
              <div>
                <dt>Testing level</dt>
                <dd>
                  <strong>
                    {TESTING_LEVEL_NAMES[letter.testingLevel as TestingLevel] ??
                      letter.testingLevel}
                  </strong>
                </dd>
              </div>
              <div>
                <dt>Included domain scope</dt>
                <dd>
                  <DomainScopeCards domains={letter.domains} domain={letter.domain} />
                  {letter.ipAddresses && letter.ipAddresses.length > 0 && (
                    <span>IPs {letter.ipAddresses.join(', ')}</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Excluded hosts</dt>
                <dd>
                  <strong>{letter.exclusions.join(', ') || 'None declared'}</strong>
                </dd>
              </div>
              <div>
                <dt>Approved by the owner</dt>
                <dd>
                  <strong>{date(letter.confirmedAt)}</strong>
                </dd>
              </div>
            </dl>
            <p className={styles.note}>
              Other providers need their own permission from the owner. Testing also requires an
              active, approved engagement.
            </p>
          </section>
          <section className={styles.downloads} aria-labelledby="downloads-title">
            <h2 id="downloads-title" className={styles.downloadsTitle}>
              <FileDown size={20} aria-hidden="true" />
              Downloads
            </h2>
            <div className={styles.downloadActions}>
              <a
                className={buttonClass('primary')}
                href={api.domainLetterPdf(letter.domain)}
                download
              >
                Download certificate (PDF)
              </a>
              <Button
                onPress={() => download(letter, `traceira-authorization-${letter.domain}.json`)}
              >
                Download signed letter (.json)
              </Button>
            </div>
          </section>
        </>
      )}
      <Disclosure title="Check it yourself" level={3}>
        <ol className={styles.publishSteps}>
          <li>
            <p className={styles.stepHead}>
              <strong>Read the record from the owner’s DNS</strong>
            </p>
            <dl className={styles.recordList}>
              <div>
                <dt>Command</dt>
                <dd>
                  <code className={styles.recordValue}>{dig}</code>
                  <CopyButton value={dig} what="DNS lookup" label="Copy" />
                </dd>
              </div>
            </dl>
          </li>
          <li>
            <p className={styles.stepHead}>
              <strong>Check the Ed25519 signature</strong>
              <span>
                Against Traceira’s{' '}
                <a href={`${letter?.issuer ?? 'https://authz.traceira.com'}/.well-known/jwks.json`}>
                  public signing keys
                </a>
              </span>
            </p>
          </li>
          {letter && (
            <li>
              <p className={styles.stepHead}>
                <strong>Compare the terms digest</strong>
                <span>
                  <code>{letter.termsHash}</code>
                </span>
              </p>
            </li>
          )}
        </ol>
      </Disclosure>
    </PublicLayout>
  );
}
